import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { CellClassParams } from 'ag-grid-community';
import {
  InfiniteScrollingService,
  ReusableStreamProperty,
  equalsCompare,
  numberComparer
} from '@ipreo/muni-shared';
import { BarChartLegend } from '../../../../shared/models/charts/chart-data-types';
import { MonitorRetailPageService } from '../../../services/monitor-retail-page.service';
import { NegativeCasesService } from '../../../../gameday/services/negative-cases.service';
import { RETAIL_TOTAL } from '../../../services/monitor-base-page.service';
import { ColumnDefinition } from '../../../../models/column';
import { MonitorOverviewViewItem } from '../../../models/monitor';
import {
  performFinalTransformations,
  transformTotals
} from '../../../mappers/monitor-base-page.calculations';
import {
  MonitorBasePageComponent,
  monitorBasePageSassVariables
} from '../base-monitor-tab.component';

export const DISABLE_RETAIL_TAB_EVENT = 'DISABLE_RETAIL_TAB_EVENT';

// @dynamic
@Component({
  providers: [MonitorRetailPageService],
  selector: 'muni-gameday-monitor-retail-page',
  templateUrl: './monitor-retail-page.template.html',
  styleUrls: ['../base-monitor-tab.styles.scss'],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['dataSource'],
  // eslint-disable-next-line @angular-eslint/no-outputs-metadata-property
  outputs: ['maturityClick'],
  standalone: false,
})
export class MonitorRetailPageComponent
  extends MonitorBasePageComponent<MonitorRetailPageService>
  implements OnDestroy, OnInit {
  static columnDefinition: ColumnDefinition[] = [
    {
      field: 'total',
      headerName: RETAIL_TOTAL,
      label: RETAIL_TOTAL,
      comparator: numberComparer,
      position: 999,
      suppressHeaderMenuButton: true,
      type: 'numericColumn'
    },
    {
      field: 'balance',
      headerName: 'Retail Balance',
      label: 'Retail Balance',
      comparator: numberComparer,
      position: 1001,
      width: 110,
      minWidth: 110,
      maxWidth: 110,
      cellStyle: ({ value }: CellClassParams) => {
        return value > 0
          ? { color: monitorBasePageSassVariables.colorRedDarker }
          : null;
      },
      suppressHeaderMenuButton: true,
      type: 'numericColumn'
    },
    {
      field: 'subscription',
      headerName: 'Retail Subscription',
      label: 'Retail Subscription',
      comparator: numberComparer,
      position: 1002,
      width: 130,
      minWidth: 130,
      maxWidth: 130,
      cellStyle: ({ value }: CellClassParams) => {
        return value < 1
          ? { color: monitorBasePageSassVariables.colorRedDarker }
          : null;
      },
      suppressHeaderMenuButton: true,
      type: 'numericColumn'
    },
    ...MonitorBasePageComponent.columnDefinition
  ];

  static notCategories: string[] = MonitorRetailPageComponent.columnDefinition.map(
    (item) => item.field
  );

  @Output()
  disable = new EventEmitter<{ payload: boolean; type: string }>();

  public categoriesSubscription: Subscription;

  public categories = this.monitorPageService.getCategories();

  @ReusableStreamProperty
  public data: Observable<
    MonitorOverviewViewItem[]
  > = this.monitorPageService
    .getDataCombinedByMaturities()
    .pipe(
      map((data) =>
        data.map((item) =>
          performFinalTransformations(
            item,
            MonitorRetailPageComponent.notCategories
          )
        )
      )
    );

  @ReusableStreamProperty
  public totals: Observable<MonitorOverviewViewItem> = this.monitorPageService
    .calculateTotals()
    .pipe(
      map((totals) =>
        transformTotals(totals, MonitorRetailPageComponent.notCategories)
      )
    );

  @ReusableStreamProperty
  public columnDefinitions: Observable<
    ColumnDefinition[]
  > = this.monitorPageService
    .getColumnDefinitions(
      MonitorRetailPageComponent.columnDefinition,
      this.categories
    )
    .pipe(distinctUntilChanged(equalsCompare)); // checked, makes sense

  constructor(
    monitorRetailPageService: MonitorRetailPageService,
    negativeCasesService: NegativeCasesService,
    infiniteScrollingService: InfiniteScrollingService
  ) {
    super(
      monitorRetailPageService,
      negativeCasesService,
      infiniteScrollingService
    );
  }

  public ngOnDestroy(): void {
    this.categoriesSubscription.unsubscribe();
    super.onDestroy();
  }

  public ngOnInit(): void {
    this.categoriesSubscription = this.monitorPageService
      .getCategories()
      .pipe(
        map(
          (categories: BarChartLegend[]) =>
            !this.monitorPageService.containsNotRetailsCategories(categories)
        ),
        distinctUntilChanged()
      )
      .subscribe((isDisabled: boolean) =>
        this.disable.emit({
          type: DISABLE_RETAIL_TAB_EVENT,
          payload: isDisabled
        })
      );
  }
}
